import { Injectable, inject, signal } from "@angular/core";
import { catchError, defer, firstValueFrom, of } from "rxjs";
import { SharedService } from "./shared.service";
import { IGetPaymentTypesResultData } from "./model/pos.api.model";
import { PaymentContext, PaymentGroup, isPaymentAvailable, isPaymentInGroup } from "../models/payment-availability.model";
import { PaymentDialogModel } from "../models/payment-dialog.model";

// Page-scoped: a different page or open dialog cannot change this page's context.
@Injectable()
export class PaymentAvailabilityService {
  private readonly shared = inject(SharedService);
  readonly payments = signal<IGetPaymentTypesResultData[]>([]);
  readonly ready = signal(false);
  private requestVersion = 0;

  async refresh(): Promise<void> {
    const version = ++this.requestVersion;
    this.ready.set(false);
    this.payments.set([]);
    const payments = await firstValueFrom(defer(() => this.shared.getAllPaymentTypes(true)).pipe(
      catchError(() => of([] as IGetPaymentTypesResultData[])),
    ));
    if (version === this.requestVersion) {
      this.payments.set(payments ?? []);
      this.ready.set(true);
    }
  }

  has(context: PaymentContext, group: PaymentGroup): boolean {
    return this.ready() && this.payments().some(payment => isPaymentAvailable(payment, context) && isPaymentInGroup(payment, group));
  }

  async prepare(data: PaymentDialogModel, context: PaymentContext): Promise<PaymentDialogModel> {
    await this.refresh();
    return {
      ...data,
      paymentContext: context,
      allowedPaymentNames: this.payments().filter(payment => isPaymentAvailable(payment, context)).map(payment => payment.Name),
    };
  }
}
